/*
 * Copyright (c) CIRROM 2021.
 */

import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";

export interface GridTab {
  name: string;
  label: string;
  tooltip?: string;
  totalItems?: number;
  filters: any;
}

/**
 * GenericGridTabs Component
 *
 * This component provides a tab navigation for a grid with suggested changes.
 *
 * @param {object} props - Component props.
 * @param {string} [props.selectedTab] - The initially selected tab.
 * @param {function} props.onSelectionChanged - Callback when tab selection changes.
 * @param {GridTab[]} props.gridTabs - An array of tab configurations.
 * @param {string} props.defaultSelectedTab - The default tab to be selected.
 * @returns {JSX.Element} - Rendered component.
 */
export function GenericGridTabs(
  props: Readonly<{
    selectedTab?: string;
    onSelectionChanged: (filterModel: any, selectedTab: string) => void;
    gridTabs: GridTab[];
    defaultSelectedTab: string;
  }>
) {
  const { onSelectionChanged, selectedTab, gridTabs, defaultSelectedTab } =
    props;
  const [localSelectedTab, setLocalSelectedTab] = useState<string>(
    selectedTab || defaultSelectedTab
  );

  useEffect(() => {
    if (selectedTab && selectedTab !== localSelectedTab) {
      setLocalSelectedTab(selectedTab);
    }
  }, [selectedTab]);

  useEffect(() => {
    if (localSelectedTab) {
      // Call onSelectionChanged callback with filter and selected tab

      onSelectionChanged(
        filterSuggestedChangesGrid(localSelectedTab),
        localSelectedTab
      );
    }
  }, [localSelectedTab]);

  /**
   * filterSuggestedChangesGrid Function
   *
   * Get the filter configuration for the selected tab.
   *
   * @param {string} tabSelection - The selected tab's name.
   * @returns {object} - The filter configuration for the selected tab.
   */
  const filterSuggestedChangesGrid = (tabSelection) => {
    for (let tab of gridTabs) {
      if (tab.name === tabSelection) {
        return tab.filters;
      }
    }
  };

  /**
   * getTabDisplayLabel Function
   *
   * Generate a tab label JSX element with optional total items count.
   *
   * The count used to be `75%` of the label - about 9.75px, well under the 13px floor -
   * coloured red[700] when there was work and green[700] when there was none. Two
   * problems: those literals ignore dark mode and the six non-CIRROM variants, and
   * red-for-pending reads as an error state when a queue with items in it is simply an
   * ordinary working day.
   *
   * A count is now just a count: secondary text, at the label's own size. Whether it
   * matters is a judgement the steward makes, not one the colour makes for them.
   *
   * @param {string} tabLabel - The label of the tab.
   * @param {number} [totalItems] - Optional total items count.
   * @returns {JSX.Element} - The tab label JSX element.
   */
  const getTabDisplayLabel = (tabLabel: string, totalItems?: number) => {
    if (totalItems !== undefined) {
      return (
        <>
          <span>{tabLabel}</span>
          <Box
            component={"span"}
            sx={{
              fontWeight: 600,
              color: "text.secondary",
              marginLeft: "4px",
            }}
          >
            ({totalItems.toLocaleString()})
          </Box>
        </>
      );
    }
    return tabLabel;
  };

  return (
    <Tabs
      value={localSelectedTab}
      onChange={(_event, newValue) => {
        setLocalSelectedTab(newValue);
      }}
      variant={"scrollable"}
      scrollButtons={"auto"}
      sx={{
        "& .MuiTab-root:first-of-type": { paddingLeft: 0, minWidth: 0 },

        "& .MuiTab-root.Mui-selected": {
          color: "text.primary",
          fontWeight: 600,
        },
      }}
    >
      {gridTabs.map((gridTab) => {
        return (
          <Tab
            key={gridTab.label}
            value={gridTab.name}
            label={
              gridTab.tooltip ? (
                // Use Tooltip for tab label with tooltip
                <Tooltip title={gridTab.tooltip}>
                  <span>
                    {getTabDisplayLabel(gridTab.label, gridTab.totalItems)}
                  </span>
                </Tooltip>
              ) : (
                <span>
                  {getTabDisplayLabel(gridTab.label, gridTab.totalItems)}
                </span>
              )
            }
          />
        );
      })}
    </Tabs>
  );
}
